/**
 * Web implementation of the RealtimeRoomAdapter interface,
 * backed by `livekit-client` (plain JS SDK — no React wrapper).
 *
 * All LiveKit-specific details are encapsulated here — controllers and
 * components interact only through the RealtimeRoomAdapter contract.
 */

import { Room, RoomEvent, Track, type RpcInvocationData, type RemoteParticipant, type RemoteTrackPublication, type TranscriptionSegment as LKTranscriptionSegment, type Participant, LocalParticipant } from "livekit-client";

import type { RealtimeRoomAdapter, ConnectionState, RpcInvocation, TextStreamInfo, TranscriptionSegment } from "@ringg/core";
import { isAgentIdentity } from "@ringg/core";

type Unsubscribe = () => void;

export class LiveKitWebAdapter implements RealtimeRoomAdapter {
  private room: Room;
  private cleanupFns: Unsubscribe[] = [];
  /** Container for <audio> elements that play remote participant audio tracks */
  private _audioContainer: HTMLDivElement | null = null;
  private _audioElements = new Map<string, HTMLAudioElement>();

  constructor(roomOptions?: ConstructorParameters<typeof Room>[0]) {
    this.room = new Room({
      dynacast: true,
      adaptiveStream: true,
      ...roomOptions,
    });
    this._setupAudioRenderer();
  }

  /**
   * Equivalent of React's <RoomAudioRenderer /> — automatically attaches/detaches
   * <audio> elements for remote participants' audio tracks.
   * This is essential for hearing the agent's voice in audio mode.
   */
  private _setupAudioRenderer(): void {
    // Create a hidden container for audio elements
    this._audioContainer = document.createElement("div");
    this._audioContainer.style.display = "none";
    this._audioContainer.setAttribute("data-ringg-audio", "");
    document.body.appendChild(this._audioContainer);

    // When a remote track is subscribed, attach audio
    const onTrackSubscribed = (track: Track, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) {
        const el = track.attach();
        el.id = `ringg-audio-${participant.identity}-${track.sid}`;
        this._audioElements.set(el.id, el);
        this._audioContainer?.appendChild(el);
      }
    };

    // When a remote track is unsubscribed, detach audio
    const onTrackUnsubscribed = (track: Track, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) {
        track.detach().forEach((el) => el.remove());
        const id = `ringg-audio-${participant.identity}-${track.sid}`;
        this._audioElements.delete(id);
      }
    };

    this.room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    this.room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);

    this.cleanupFns.push(() => {
      this.room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      this.room.off(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    });
  }

  // ─── Connection ─────────────────────────────────────────────────────────

  getConnectionState(): ConnectionState {
    switch (this.room.state) {
      case "connected":
        return "connected";
      case "reconnecting":
        return "reconnecting";
      case "disconnected":
        return "disconnected";
      default:
        return "disconnected";
    }
  }

  async connect(url: string, token: string): Promise<void> {
    await this.room.connect(url, token);
  }

  disconnect(): void {
    try {
      this.room.disconnect();
    } catch {
      // Suppress "cannot send signal request before connected" — races with reconnection
    }
  }

  // ─── Microphone ─────────────────────────────────────────────────────────

  async setMicrophoneEnabled(enabled: boolean): Promise<void> {
    await this.room.localParticipant.setMicrophoneEnabled(enabled);
  }

  isMicrophoneEnabled(): boolean {
    return this.room.localParticipant.isMicrophoneEnabled;
  }

  // ─── Audio Session (no-op on web) ───────────────────────────────────────

  async configureAudio(): Promise<void> {
    // Web browsers handle audio routing natively — no configuration needed.
  }

  async startAudioSession(): Promise<void> {
    // Ensure browser autoplay policy allows audio playback.
    // This must be called from a user gesture context (e.g. button click).
    await this.room.startAudio();
  }

  async stopAudioSession(): Promise<void> {
    // No-op on web.
  }

  // ─── Messaging ──────────────────────────────────────────────────────────

  async sendChatMessage(text: string): Promise<void> {
    // Primary: new Data Streams API on topic "lk.chat"
    await this.room.localParticipant.sendText(text, { topic: "lk.chat" });

    // Legacy fallback: publishData with JSON-encoded payload on "lk-chat-topic"
    try {
      const legacyPayload = JSON.stringify({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        timestamp: Date.now(),
        message: text,
      });
      const encoded = new TextEncoder().encode(legacyPayload);
      await this.room.localParticipant.publishData(encoded, {
        reliable: true,
        topic: "lk-chat-topic",
      });
    } catch {
      // Legacy send is best-effort — ignore errors
    }
  }

  // ─── RPC ────────────────────────────────────────────────────────────────

  registerRpcMethod(method: string, handler: (data: RpcInvocation) => Promise<string>): void {
    this.room.registerRpcMethod(method, async (data: RpcInvocationData) => {
      return handler({
        method,
        payload: data.payload,
        callerIdentity: data.callerIdentity,
      });
    });
  }

  unregisterRpcMethod(method: string): void {
    this.room.unregisterRpcMethod(method);
  }

  async performRpc(destinationIdentity: string, method: string, payload: string): Promise<string> {
    return this.room.localParticipant.performRpc({
      destinationIdentity,
      method,
      payload,
    });
  }

  // ─── Text Streams ───────────────────────────────────────────────────────

  registerTextStreamHandler(topic: string, handler: (info: TextStreamInfo) => void): void {
    this.room.registerTextStreamHandler(topic, async (reader, participantInfo) => {
      try {
        const message = await reader.readAll();
        handler({
          message,
          participantIdentity: participantInfo.identity ?? "",
        });
      } catch (err) {
        console.error("[LiveKitWebAdapter] Error reading text stream:", err);
      }
    });
  }

  unregisterTextStreamHandler(topic: string): void {
    this.room.unregisterTextStreamHandler(topic);
  }

  // ─── Event Subscriptions ────────────────────────────────────────────────

  onConnectionStateChange(handler: (state: ConnectionState) => void): Unsubscribe {
    const mapState = (): ConnectionState => this.getConnectionState();

    const onConnected = () => handler(mapState());
    const onDisconnected = () => handler("disconnected");
    const onReconnecting = () => handler("reconnecting");
    const onReconnected = () => handler(mapState());

    this.room.on(RoomEvent.Connected, onConnected);
    this.room.on(RoomEvent.Disconnected, onDisconnected);
    this.room.on(RoomEvent.Reconnecting, onReconnecting);
    this.room.on(RoomEvent.Reconnected, onReconnected);

    const unsub = () => {
      this.room.off(RoomEvent.Connected, onConnected);
      this.room.off(RoomEvent.Disconnected, onDisconnected);
      this.room.off(RoomEvent.Reconnecting, onReconnecting);
      this.room.off(RoomEvent.Reconnected, onReconnected);
    };

    this.cleanupFns.push(unsub);
    return unsub;
  }

  onParticipantDisconnected(handler: (identity: string) => void): Unsubscribe {
    const onDisconnect = (participant: RemoteParticipant) => {
      handler(participant.identity);
    };

    this.room.on(RoomEvent.ParticipantDisconnected, onDisconnect);

    const unsub = () => {
      this.room.off(RoomEvent.ParticipantDisconnected, onDisconnect);
    };

    this.cleanupFns.push(unsub);
    return unsub;
  }

  onDisconnected(handler: () => void): Unsubscribe {
    this.room.on(RoomEvent.Disconnected, handler);

    const unsub = () => {
      this.room.off(RoomEvent.Disconnected, handler);
    };

    this.cleanupFns.push(unsub);
    return unsub;
  }

  onTranscription(handler: (segment: TranscriptionSegment) => void): Unsubscribe {
    const onSegments = (segments: LKTranscriptionSegment[], participant?: Participant) => {
      if (!participant) return;

      for (const seg of segments) {
        handler({
          id: seg.id,
          text: seg.text,
          isFinal: seg.final,
          participantIdentity: participant.identity,
          isAgent: !(participant instanceof LocalParticipant),
        });
      }
    };

    this.room.on(RoomEvent.TranscriptionReceived, onSegments);

    const unsub = () => {
      this.room.off(RoomEvent.TranscriptionReceived, onSegments);
    };

    this.cleanupFns.push(unsub);
    return unsub;
  }

  // ─── Participant Queries ────────────────────────────────────────────────

  getAgentIdentity(): string | null {
    for (const [, participant] of this.room.remoteParticipants) {
      if (isAgentIdentity(participant.identity)) {
        return participant.identity;
      }
    }
    return null;
  }

  getLocalIdentity(): string {
    return this.room.localParticipant.identity;
  }

  // ─── Cleanup ────────────────────────────────────────────────────────────

  dispose(): void {
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];

    // Remove all attached audio elements
    this._audioElements.forEach((el) => {
      el.pause();
      el.srcObject = null;
      el.remove();
    });
    this._audioElements.clear();
    this._audioContainer?.remove();
    this._audioContainer = null;

    try {
      this.room.disconnect();
    } catch {
      // Safe to ignore — room may already be disconnected
    }
  }
}
